// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — useMyPAOrg Hook
// Resolves the logged-in user's PA Grants organization
// Checks contactEmails first, then falls back to individual email fields
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { usePAGrantsStore } from '../store/paGrantsStore';
import type { PAOrganization } from '../types/pagrants';

export function useMyPAOrg(): PAOrganization | null {
  const user = useAuthStore(s => s.user);
  const organizations = usePAGrantsStore(s => s.organizations);

  return useMemo(() => {
    if (!user?.email || organizations.length === 0) return null;
    const email = user.email.toLowerCase();

    // Match 1: contactEmails field (comma-separated list of authorized emails)
    const byContacts = organizations.find(org => {
      if (!org.contactEmails) return false;
      return org.contactEmails
        .split(',')
        .some(e => e.trim().toLowerCase() === email);
    });
    if (byContacts) return byContacts;

    // Match 2: primaryEmail field
    const byPrimary = organizations.find(
      org => org.primaryEmail?.toLowerCase() === email
    );
    if (byPrimary) return byPrimary;

    // Match 3: altEmail field
    const byAlt = organizations.find(
      org => org.altEmail?.toLowerCase() === email
    );
    if (byAlt) return byAlt;

    // Match 4: garEmail field
    const byGar = organizations.find(
      org => org.garEmail?.toLowerCase() === email
    );
    if (byGar) return byGar;

    // Match 5: spaoEmail field
    const bySpao = organizations.find(
      org => org.spaoEmail?.toLowerCase() === email
    );
    if (bySpao) return bySpao;

    return null;
  }, [user?.email, organizations]);
}
